import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { sharedApi, DEVICE_ID_HEADERS } from "@/lib/api";

/**
 * Iter-482 — "Continue with Apple" on the classifieds site (mirrors the
 * bazaar site's flow). Apple JS popup (usePopup: true) → POST the signed
 * id_token to the shared backend `/auth/login/apple` (platform: "web",
 * Service ID audience com.allsale.shop.signin) → standard AuthResponse
 * stored exactly like email login. Apple only returns the user's name on
 * the FIRST authorization, so it's forwarded when present.
 * Requires the site domain + /auth/apple/callback return URL to be
 * registered on the Apple Service ID (founder console step).
 */

const APPLE_SCRIPT_SRC =
  "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
const SERVICE_ID = process.env.REACT_APP_APPLE_SERVICE_ID;

let scriptPromise = null;
function loadAppleSdk() {
  if (window.AppleID) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = APPLE_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error("Apple sign-in is unavailable right now."));
    };
    document.head.appendChild(script);
  });
  return scriptPromise;
}

function randomValue() {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
}

export default function AppleSignInButton({ label = "Continue with Apple", nextPath = "/" }) {
  const { setSessionFromToken } = useAuth();
  const nav = useNavigate();
  const [pending, setPending] = useState(false);
  const redirectUri = useRef(`${window.location.origin}/auth/apple/callback`);

  // Backend-controlled rollout: only render when the shared backend's
  // site config flags apple_signin on (defaults ON if config unreadable).
  const flagQ = useQuery({
    queryKey: ["site-config-flags"],
    queryFn: async () => (await sharedApi.get("/site/config")).data?.feature_flags || null,
    staleTime: 10 * 60_000,
    retry: 0,
  });
  const enabled = flagQ.data ? flagQ.data.apple_signin !== false : true;

  // Preload the SDK so the popup opens instantly from the click gesture.
  useEffect(() => {
    if (SERVICE_ID) loadAppleSdk().catch(() => {});
  }, []);

  if (!SERVICE_ID || !enabled) return null;

  const onClick = async () => {
    setPending(true);
    try {
      await loadAppleSdk();
      const state = randomValue();
      window.AppleID.auth.init({
        clientId: SERVICE_ID,
        scope: "name email",
        redirectURI: redirectUri.current,
        state,
        nonce: randomValue(),
        usePopup: true,
      });
      const res = await window.AppleID.auth.signIn();
      const auth = res?.authorization;
      if (!auth?.id_token) throw new Error("Apple didn't return a credential.");
      if (auth.state && auth.state !== state) throw new Error("Apple sign-in could not be verified.");

      // Name arrives on the FIRST authorization only.
      const first = res?.user?.name?.firstName?.trim() || "";
      const last = res?.user?.name?.lastName?.trim() || "";
      const fullName = [first, last].filter(Boolean).join(" ") || undefined;

      const { data } = await sharedApi.post("/auth/login/apple", {
        identity_token: auth.id_token,
        authorization_code: auth.code || undefined,
        redirect_uri: redirectUri.current,
        platform: "web",
        ...(fullName ? { full_name: fullName } : {}),
      }, DEVICE_ID_HEADERS());
      if (!data?.access_token) throw new Error("Apple sign-in failed — please try again.");
      await setSessionFromToken(data.access_token, data.user);
      toast.success("Signed in with Apple");
      nav(nextPath || "/");
    } catch (e) {
      // The SDK rejects with {error: "popup_closed_by_user"} on cancel.
      const code = e?.error || "";
      if (code === "popup_closed_by_user" || code === "user_cancelled_authorize") {
        toast("Apple sign-in cancelled — no worries, try again anytime.");
      } else {
        const msg = e?.response?.data?.detail || e?.message || "Apple sign-in didn't complete — please try again.";
        toast.error(typeof msg === "string" ? msg : "Apple sign-in didn't complete — please try again.");
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={pending}
      data-testid="apple-signin-btn"
      aria-label={label}
      className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-black hover:bg-black/85 text-white font-semibold py-3 transition-colors disabled:opacity-60"
    >
      <AppleGlyph />
      <span>{pending ? "Opening Apple…" : label}</span>
    </button>
  );
}

/** Official Apple mark — flat SVG per Apple HIG, no external asset. */
function AppleGlyph({ size = 17 }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 384 512" aria-hidden="true" fill="currentColor">
      <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.9-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
    </svg>
  );
}
